import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AppDispatch, RootState } from '../../Redux/Store';
import { fetchReservationDashboard } from '../../Redux/Reservation/reservationApi';
import {
  DashboardBucket,
  DashboardGranularity,
  RESERVATION_STATUS_LABEL,
  ReservationStatus,
  toDateString,
} from '../../types/reservation';

/**
 * ダッシュボードのステータス色（台帳/一覧の RESERVATION_STATUS_COLOR と揃えた具体色）。
 * 積み上げバーのセグメントは MUI Chip の色名ではなく CSS 色が必要なため theme パレットと同じ hex を持つ。
 * 承認待ち=オレンジ / 確定=緑 / 来店済み=青 / 不来店=赤 / キャンセル・却下=グレー。
 */
const STATUS_BAR_COLOR: Record<ReservationStatus, string> = {
  REQUESTED: '#ff9800', // warning
  CONFIRMED: '#4caf50', // success
  COMPLETED: '#2196f3', // info
  NO_SHOW: '#f44336', // error
  CANCELLED: '#9e9e9e', // grey（default 相当）
  REJECTED: '#bdbdbd', // 薄いグレー（却下）
  // Phase 2（3DSデポジット）。通常は台帳/一覧に出さないが集計で来うるため色だけ用意（STATUS_ORDER には含めない）。
  PENDING_PAYMENT: '#ffb74d', // 決済待ち（薄いオレンジ）
  EXPIRED: '#cfcfcf', // 期限切れ（薄いグレー）
};

/** 積み上げ・凡例の並び（要対応の REQUESTED を先頭に、終端ステータスは後ろ） */
const STATUS_ORDER: ReservationStatus[] = [
  'REQUESTED',
  'CONFIRMED',
  'COMPLETED',
  'NO_SHOW',
  'CANCELLED',
  'REJECTED',
];

const GRANULARITY_LABEL: Record<DashboardGranularity, string> = {
  HOUR: '時間別',
  DAY: '日別',
  MONTH: '月別',
};

/** 高さの基準（バーの高さに何を使うか） */
type Metric = 'people' | 'count';

interface Props {
  storeId: string;
  /** 現在のステータスフィルタ（null=すべて）。特定ステータス時は単色バー、null 時は積み上げバー。 */
  statusFilter: ReservationStatus | null;
  /**
   * 日別リスト等と anchor 日付を共有する。granularity 切替時の初期 anchor に使う。
   * ダッシュボード内の anchor ナビは独立管理し、この値には書き戻さない（一覧の選択日を勝手に動かさないため）。
   */
  baseDate: string;
}

/** 曜日（日曜=0） */
const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'];

/** anchor(YYYY-MM-DD) から MONTH 用の 'YYYY年M月' ラベルを作る */
const monthLabel = (anchor: string): string => {
  const [y, m] = anchor.split('-');
  return `${y}年${parseInt(m, 10)}月`;
};

/**
 * バーのX軸ラベル。日別は年を含めると軸が崩れるため、key('YYYY-MM-DD')から
 * 'M/D(曜)' を組んで年を落とす（BE の label が年付きISOでも堅牢に整形する）。
 * 時間別・月別は BE の label（'18時' / '2026-07' 等）をそのまま使う。
 */
const bucketAxisLabel = (granularity: DashboardGranularity, b: DashboardBucket): string => {
  if (granularity === 'DAY') {
    const d = new Date(`${b.key}T00:00:00`);
    if (!isNaN(d.getTime())) {
      return `${d.getMonth() + 1}/${d.getDate()}(${WEEKDAY_JA[d.getDay()]})`;
    }
  }
  return b.label;
};

/** 日付文字列を月単位で移動（deltaMonths 分） */
const shiftMonth = (anchor: string, deltaMonths: number): string => {
  const d = new Date(`${anchor}T00:00:00`);
  d.setDate(1);
  d.setMonth(d.getMonth() + deltaMonths);
  return toDateString(d);
};

/** 日付文字列を日単位で移動 */
const shiftDay = (anchor: string, deltaDays: number): string => {
  const d = new Date(`${anchor}T00:00:00`);
  d.setDate(d.getDate() + deltaDays);
  return toDateString(d);
};

/**
 * 予約ダッシュボード（統合契約 B）。
 * 粒度トグル（時間別/日別/月別）＋ anchor ナビ＋ステータスフィルタ連動の積み上げ/単色バー。
 * BE 集計 API を消費（旧BEで 404/未対応でもエラー表示のみ・画面は壊さない）。
 */
const ReservationDashboard: React.FC<Props> = ({ storeId, statusFilter, baseDate }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { dashboard, dashboardLoading, dashboardError } = useSelector(
    (state: RootState) => state.reservation,
  );

  const [granularity, setGranularity] = useState<DashboardGranularity>('HOUR');
  // anchor はダッシュボード内で独立管理（初期値は一覧の選択日）。
  const [anchor, setAnchor] = useState<string>(baseDate);
  // 高さの基準（人数 or 件数）
  const [metric, setMetric] = useState<Metric>('people');

  // 一覧の選択日が変わったら HOUR の anchor を追従させる（日別リストと時間別ダッシュボードの整合）。
  useEffect(() => {
    setAnchor(baseDate);
  }, [baseDate]);

  const load = useCallback(() => {
    if (!storeId) return;
    dispatch(fetchReservationDashboard({ storeId, granularity, anchor }));
  }, [dispatch, storeId, granularity, anchor]);

  useEffect(() => {
    load();
  }, [load]);

  // anchor ナビ（時間別=日/日別=月/月別=範囲を1つずつ）
  const moveAnchor = (dir: -1 | 1) => {
    if (granularity === 'HOUR') setAnchor(shiftDay(anchor, dir));
    else if (granularity === 'DAY') setAnchor(shiftMonth(anchor, dir));
    else setAnchor(shiftMonth(anchor, dir * 12));
  };

  const anchorNavLabel = useMemo(() => {
    if (granularity === 'HOUR') {
      const d = new Date(`${anchor}T00:00:00`);
      return `${d.getMonth() + 1}/${d.getDate()}(${WEEKDAY_JA[d.getDay()]})`;
    }
    if (granularity === 'DAY') return monthLabel(anchor);
    // MONTH: anchor月-1 .. anchor月+12
    const from = shiftMonth(anchor, -1);
    const to = shiftMonth(anchor, 12);
    return `${monthLabel(from)}〜${monthLabel(to)}`;
  }, [granularity, anchor]);

  // 現在のフィルタに応じた表示ステータス（null=全ステータス積み上げ / 特定=単色）
  const shownStatuses = useMemo<ReservationStatus[]>(
    () => (statusFilter ? [statusFilter] : STATUS_ORDER),
    [statusFilter],
  );

  const buckets = useMemo<DashboardBucket[]>(() => dashboard?.buckets ?? [], [dashboard]);

  // バケットの合計値（metric 基準）。空バケット除外の判断とスケール算出に使う。
  const bucketTotal = useCallback(
    (b: DashboardBucket): number =>
      shownStatuses.reduce((sum, s) => sum + (b.byStatus[s]?.[metric] ?? 0), 0),
    [shownStatuses, metric],
  );

  // ピーク（バー高さ正規化用）。最低1で0除算回避。
  const peak = Math.max(1, ...buckets.map((b) => bucketTotal(b)));

  // 全ステータス合計（サマリ表示）。フィルタ関係なく全体像を出す。
  const grandTotals = useMemo(() => {
    let count = 0;
    let people = 0;
    buckets.forEach((b) => {
      shownStatuses.forEach((s) => {
        count += b.byStatus[s]?.count ?? 0;
        people += b.byStatus[s]?.people ?? 0;
      });
    });
    return { count, people };
  }, [buckets, shownStatuses]);

  const metricUnit = metric === 'people' ? '名' : '件';
  const hasData = buckets.some((b) => bucketTotal(b) > 0);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      {/* コントロール行: 粒度トグル / anchor ナビ / 高さ基準トグル */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1.5,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={granularity}
          onChange={(_, v: DashboardGranularity | null) => v && setGranularity(v)}
        >
          {(['HOUR', 'DAY', 'MONTH'] as DashboardGranularity[]).map((g) => (
            <ToggleButton key={g} value={g} aria-label={GRANULARITY_LABEL[g]}>
              {GRANULARITY_LABEL[g]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            aria-label="前へ"
            size="small"
            onClick={() => moveAnchor(-1)}
          >
            <ChevronLeftIcon />
          </IconButton>
          {granularity === 'HOUR' ? (
            <TextField
              type="date"
              size="small"
              value={anchor}
              onChange={(e) => e.target.value && setAnchor(e.target.value)}
            />
          ) : (
            <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'center' }}>
              {anchorNavLabel}
            </Typography>
          )}
          <IconButton
            aria-label="次へ"
            size="small"
            onClick={() => moveAnchor(1)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={metric}
          onChange={(_, v: Metric | null) => v && setMetric(v)}
          aria-label="バーの高さ基準"
        >
          <ToggleButton value="people" aria-label="人数で表示">
            人数
          </ToggleButton>
          <ToggleButton value="count" aria-label="件数で表示">
            件数
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* サマリ + 凡例 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {statusFilter
            ? `${RESERVATION_STATUS_LABEL[statusFilter]}: ${grandTotals.count}件 / ${grandTotals.people}名`
            : `合計 ${grandTotals.count}件 / ${grandTotals.people}名`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {shownStatuses.map((s) => (
            <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  backgroundColor: STATUS_BAR_COLOR[s],
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {RESERVATION_STATUS_LABEL[s]}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {dashboardError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          集計の取得に失敗しました（{dashboardError}）
        </Typography>
      )}

      {dashboardLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !hasData ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          この期間の予約はありません
        </Typography>
      ) : (
        // 自前の積み上げバー（MUI Box のみ）。横スクロールで全バケットを見せる。
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0.75,
            overflowX: 'auto',
            pb: 1,
          }}
        >
          {buckets.map((b) => {
            const total = bucketTotal(b);
            const heightRatio = total / peak;
            return (
              <Box
                key={b.key}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 36,
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, height: 16 }}>
                  {total > 0 ? `${total}${metricUnit}` : ''}
                </Typography>
                {/* バー本体（固定高の枠内に積み上げ） */}
                <Box
                  sx={{
                    width: 22,
                    height: 96,
                    display: 'flex',
                    flexDirection: 'column-reverse', // 下から積む
                    justifyContent: 'flex-start',
                    borderRadius: 0.5,
                    overflow: 'hidden',
                    backgroundColor: total > 0 ? 'transparent' : 'action.hover',
                  }}
                >
                  {shownStatuses.map((s) => {
                    const v = b.byStatus[s]?.[metric] ?? 0;
                    if (v <= 0) return null;
                    // このバケット全体の高さ(96px×heightRatio)をステータス比で分割する。
                    const segPx = Math.max(2, Math.round(96 * heightRatio * (v / total)));
                    return (
                      <Tooltip
                        key={s}
                        title={`${RESERVATION_STATUS_LABEL[s]}: ${b.byStatus[s]?.count ?? 0}件 / ${
                          b.byStatus[s]?.people ?? 0
                        }名`}
                        arrow
                      >
                        <Box
                          sx={{
                            width: '100%',
                            height: `${segPx}px`,
                            backgroundColor: STATUS_BAR_COLOR[s],
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.25, whiteSpace: 'nowrap', maxWidth: 44, textAlign: 'center' }}
                >
                  {bucketAxisLabel(granularity, b)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default ReservationDashboard;
